import React, { createContext, useContext, useEffect, useState } from "react";
import axiosClient from "./api/axiosClient";

type Role = "ADMIN" | "USER" | "GUEST";

interface AuthState {
  role: Role | null;
  username: string | null;
}

interface AuthContextType {
  auth: AuthState;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  isAdmin: boolean;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType>({
  auth: { role: null, username: null },
  login: async () => {},
  logout: () => {},
  isAdmin: false,
  isLoading: true,
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [auth, setAuth] = useState<AuthState>({ role: null, username: null });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (token) {
      axiosClient
        .get("/auth/me")
        .then((res) => {
          const username = atob(token.replace("Basic ", "")).split(":")[0];
          setAuth({ role: res.data.role as Role, username });
        })
        .catch(() => localStorage.removeItem("authToken"))
        .finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, []);

  const login = async (username: string, password: string) => {
    const token = "Basic " + btoa(`${username}:${password}`);
    localStorage.setItem("authToken", token);
    try {
      const res = await axiosClient.get("/auth/me");
      setAuth({ role: res.data.role as Role, username });
    } catch (err) {
      localStorage.removeItem("authToken");
      throw err;
    }
  };

  const logout = () => {
    localStorage.removeItem("authToken");
    setAuth({ role: null, username: null });
  };

  return (
    <AuthContext.Provider
      value={{ auth, login, logout, isAdmin: auth.role === "ADMIN", isLoading }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
